import { createContext } from 'react'
import type { AdminRole, AdminUser, LoginCredentials } from '@/types/admin'
import type { Permission } from '@/auth/permissions'

/**
 * Context lives in its own module so the provider file exports only a component
 * (keeps fast refresh working) and so guards can import the type without cycles.
 */

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous'

export type AuthContextValue = {
  status: AuthStatus
  user: AdminUser | null
  isAuthenticated: boolean
  login: (credentials: LoginCredentials) => Promise<AdminUser>
  logout: () => Promise<void>
  /** Re-reads /admin/auth/me — use after a role or profile change. */
  refreshUser: () => Promise<void>
  /**
   * Whether the signed-in staff member holds a permission.
   *
   * UX affordance only; the backend is the real authority and refuses with 403 regardless.
   */
  can: (permission: Permission) => boolean

  /** @deprecated Roles are editable data now — gate on {@link can} instead. */
  hasRole: (roles: readonly AdminRole[]) => boolean
}

export const AuthContext = createContext<AuthContextValue | null>(null)
